import { InAppContentDefinition } from 'modules/graphql/types';
import moment, { Moment } from 'moment-timezone';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { NegativeFill } from 'ui/icons';
import { DateInput, DatePickerInput, SingleDatePicker, TimeInput } from 'ui/datePicker';
import { RAW_SELECT_DATA_PORTAL_ID } from 'ui/selects';
import {
  getErrorMessage,
  validateRelativeTime,
} from 'ui/templateEdit/relativeDatePickerInput/utils';
import { colors, mediumShadow, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { DATE_FORMAT_WITH_HOUR_MINS, displayDate } from 'utils/date';
import { useClickOutside } from 'utils/hooks/useClickOutside';
import { FieldNames, SettingsReadOnlyText } from '../../common';
import { constructErrorInlineText } from '../../common/ErrorInlineText';
import { constructFieldErrors, getIsEditMode } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';

const DatePickerWrapper = styled.div`
  width: 100%;
`;

const ExpirationTimePickerWrapper = styled.div`
  ${mediumShadow};
  background-color: ${colors.white};
  width: ${pxToRem(379)};
`;

const StyledFormField = styled(FormField)`
  padding: ${spacing.half};
  width: ${pxToRem(379)};
`;

export const ExpirationTime = () => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const inAppData = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const [absoluteDate, setAbsoluteDate] = useState<Moment | null>(
    inAppData.expirationDateTime?.length ? moment(inAppData.expirationDateTime) : null,
  );
  const [absoluteTimeZone, setAbsoluteTimeZone] = useState<string | undefined>(
    inAppData.expirationDateTime?.length ? moment(inAppData.expirationDateTime).tz() : undefined,
  );
  const [relativeDate, setRelativeDate] = useState<string>(
    inAppData.expirationDuration?.length
      ? inAppData.expirationDuration.replace(/now/g, '')
      : SETTINGS_DISPLAY_CONTENT.expirationTime.defaultRelativeTime,
  );
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [relativeDateError, setRelativeDateError] = useState('');
  const isRelativeDate = !absoluteDate;

  const { hasError: hasExpirationError } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.EXPIRATION_DATE_TIME_ABSOLUTE,
  });

  const expirationErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasExpirationError,
  });

  const wrapperRef = useRef(null);
  const onAbsoluteDateChange = (newDate: Moment) => {
    setAbsoluteDate(newDate);
    setRelativeDate('');
    setCurrentMediumField('expirationDateTime', newDate.toISOString());
    setCurrentMediumField('expirationDuration', null);
  };

  const onTimeZoneChange = (timeZone: string) => {
    setAbsoluteTimeZone(timeZone);

    /**
     * In the scenario that the time zone is changed but the
     * absolute date and/or time is not set, just keep the
     * relative version. This keeps the whole template
     * editor from crashing when the date picker is opened
     * again trying to render invalid absolute values.
     */
    if (!absoluteDate) return;

    const newDate = moment(absoluteDate);
    const dateWithTimezone = moment.tz(newDate, timeZone);
    setAbsoluteDate(dateWithTimezone);
    setRelativeDate('');
    setCurrentMediumField('expirationDateTime', dateWithTimezone.toISOString());
    setCurrentMediumField('expirationDuration', null);
  };

  const onRelativeDateChange = (newDate: string) => {
    const newDateWithPlus = newDate.replace(/(\s*\+*)(\d.*)/gi, '+$2');
    const validationChecks = validateRelativeTime(newDateWithPlus, {
      start: moment(),
      end: moment().add('1', 'y'),
    });
    setRelativeDateError(getErrorMessage(validationChecks));
    setRelativeDate(newDateWithPlus);
    setAbsoluteDate(null);
    // backend expects the string values in a format of: "now+90d-2m", e.g.
    setCurrentMediumField('expirationDuration', `now${newDateWithPlus}`.replace(/\s/g, ''));
    setCurrentMediumField('expirationDateTime', null);
  };
  useClickOutside(wrapperRef, () => {
    /**
     * the gist here is: ui/dateInput (used inside ui/SingleDatePicker)
     * fires its onChange function (onDateChange down below) in the input's onBlur event handler.
     * we need to very slightly adjust when this useClickOutside function closes the datepicker,
     * so that we can make sure the onBlur event handler fires as expected.
     * without the timeout, the component is unmounted before that event handler has a chance to fire.
     *
     * see https://iterable.atlassian.net/browse/APP-5240 for context.
     */
    if (relativeDateError.length && isRelativeDate) {
      setRelativeDate('+90d');
      setCurrentMediumField('expirationDuration', `now+90d`.replace(/\s/g, ''));
      setRelativeDateError('');
    }

    const timeZonePickerDropdownPortal = document.querySelector(
      `[data-portal-id="${RAW_SELECT_DATA_PORTAL_ID}"]`,
    ); // NOTE: only one of these should exist at a time

    /**
     * If the click originated from the time zone dropdown(i.e. the dropdown is open),
     * don't close the date picker. The dropdown portal must be close before we start acknowledging
     * clicks outside of the date picker.
     */
    if (timeZonePickerDropdownPortal) {
      return;
    }

    setTimeout(() => {
      setShowDatePicker(false);
    }, 100);
  });

  const handleShowDatePicker = () => setShowDatePicker(true);

  const getReadOnlyText = () => {
    if (inAppData.expirationDateTime) {
      return displayDate(moment(inAppData.expirationDateTime), DATE_FORMAT_WITH_HOUR_MINS);
    } else if (inAppData.expirationDuration) {
      return `${
        SETTINGS_DISPLAY_CONTENT.expirationTime.relativeDatePrefix
      } ${inAppData.expirationDuration.replace(/now/g, '')}`;
    } else {
      return `${SETTINGS_DISPLAY_CONTENT.expirationTime.expirationTimeDefaultValue}`;
    }
  };

  /**
   * When mode changes, reset expirationDateTime state to keep up-to-date.
   * This is to fix UI reflecting any unsaved changes when mode change.
   * */
  useEffect(() => {
    setAbsoluteDate(
      inAppData.expirationDateTime?.length ? moment(inAppData.expirationDateTime) : null,
    );
    setAbsoluteTimeZone(
      inAppData.expirationDateTime?.length ? moment(inAppData.expirationDateTime).tz() : undefined,
    );
    setRelativeDate(
      inAppData.expirationDuration?.length
        ? inAppData.expirationDuration.replace(/now/g, '')
        : SETTINGS_DISPLAY_CONTENT.expirationTime.defaultRelativeTime,
    );
  }, [isEditModeVisible]);

  return (
    <FormField
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.expirationTime.labelText}
      hasError={hasExpirationError}
      errorText={expirationErrorText}
    >
      {isEditModeVisible ? (
        <DatePickerWrapper ref={wrapperRef} onClick={handleShowDatePicker}>
          {isRelativeDate ? (
            <InputText
              id="relative-expiration-input"
              type="text"
              value={`send time ${relativeDate}`}
            />
          ) : (
            <DatePickerInput isInvalidRange={hasExpirationError}>
              <DateInput
                date={absoluteDate}
                id="expiration-date-input"
                onDateChange={onAbsoluteDateChange}
              />
              <TimeInput
                id="expiration-time-input"
                onTimeChange={onAbsoluteDateChange}
                time={absoluteDate}
              />
            </DatePickerInput>
          )}
          {showDatePicker && (
            <ExpirationTimePickerWrapper>
              <SingleDatePicker
                date={absoluteDate as Moment}
                showTime
                defaultTimeZone={absoluteTimeZone}
                onDateChange={(newDate) => onAbsoluteDateChange(newDate as Moment)}
                onTimeZoneChange={onTimeZoneChange}
                useRelativeDate={false}
              />
              <StyledFormField
                labelText={SETTINGS_DISPLAY_CONTENT.expirationTime.relativeDateLabelText}
                hasError={!!relativeDateError.length}
                errorText={relativeDateError}
              >
                <InputText
                  type="text"
                  data-test="relative-date-input"
                  id="relative-date-picker"
                  Prefix={SETTINGS_DISPLAY_CONTENT.expirationTime.relativeDatePrefix}
                  value={relativeDate}
                  onChange={onRelativeDateChange}
                  placeholder={SETTINGS_DISPLAY_CONTENT.expirationTime.relativeDatePlaceholder}
                />
              </StyledFormField>
            </ExpirationTimePickerWrapper>
          )}
        </DatePickerWrapper>
      ) : (
        <SettingsReadOnlyText $hasError={hasExpirationError}>
          {hasExpirationError && <NegativeFill color={colors.negative100} />}
          {getReadOnlyText()}
        </SettingsReadOnlyText>
      )}
    </FormField>
  );
};
